import { HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { FileService } from '@services/lm/file.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { lastValueFrom, Subscription } from 'rxjs';
import { DocumentService } from '@services/dm/document.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { UploadResult } from '@app/interfaces/lm/file';
import { ConfirmationService } from '@services/sys/confirmation.service';

@Component({
    selector: 'app-upload',
    templateUrl: './upload.component.html',
    styleUrls: ['./upload.component.less'],
})
export class UploadComponent {
    @Input() buttonText = 'Upload new file';
    @Input() showProgress = true;
    @Input() isUploadAvatar = false;
    @Input() isUploadName = true;  // 不上传name字段,会自动根据时间戳生成name,避免上传的时候会提示文件重复
    @Input() required = false;
    @Input() path = '/file';
    @Input() maxSize = 0;
    @Input() fileLimit!: number;
    @Input() render?: TemplateRef<void>;
    @Input() nzType = undefined;
    @Input() isDocumentPart = false;
    @Input() multiple = false;
    @Input() disabled = false;
    @Input() canPreview = false;
    @Input() fileList: NzUploadFile[] = [];
    @Input() fileType!: string;
    @Input() listType!: string;
    @Input() canRemove = false;
    @Input() showUploadList = true;
    @Input() existFileReplaceNeedRemove = false;
    @Input() batchUpload = false;
    @Input() inactiveUsers: boolean = false;
    @Output() uploadSuccess = new EventEmitter();
    @Output() removeSuccess = new EventEmitter();
    @Output() upload = new EventEmitter();

    progress = 0;
    subscription: Subscription | undefined;
    loading = false;
    isVisible = false;
    cancelLoading = false;
    overwriteLoading = false;
    keepLoading = false;
    file?: File;
    timer: number | null = null;
    toUploadFile: File[] = [];
    previewImage: string | undefined = '';
    previewVisible = false;
    previewLoading = false;

    constructor(private fileService: FileService,
                private message: NzMessageService,
                private modal: NzModalService,
                private documentService: DocumentService,
                private confirmationService: ConfirmationService) {
    }

    beforeUpload = (file: File): boolean => {
        console.log('beforeUpload', file);
        // Check file name length (max 200 bytes)
        if (new TextEncoder().encode(file.name).length > 200) {
            this.message.error(`${file.name}'s name length cannot exceed 200 characters `);
            return false;
        }
        this.file = file;
        const fileTypes = this.fileType ? this.fileType.split(',') : [];
        if (fileTypes.length > 0 && !fileTypes.includes(file.type)) {
            this.message.warning(`You can only upload ${this.fileType} file!`);
            return false;
        }
        const maxBit = this.maxSize * 1024 * 1024;
        if (file.size === 0) {
            this.message.warning('File content cannot be empty.');
        } else if (this.maxSize && file.size > maxBit) {
            this.message.warning(`File size cannot exceed ${this.maxSize}M`);
        } else {
            if (!this.batchUpload) {
                // Single file upload - confirm immediately
                this.confirmationService.confirmUpdateTask(this.inactiveUsers, () => {
                    this.uploadFile(file);
                }, () => { this.handleCancel(); });
                return false;
            }
            // Batch upload - collect files first, then confirm once
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }
            this.toUploadFile.push(file);
            this.timer = window.setTimeout(() => {
                const filesToUpload = [...this.toUploadFile];
                this.toUploadFile = [];
                // Confirm once for all files
                this.confirmationService.confirmUpdateTask(this.inactiveUsers, () => {
                    if (filesToUpload.length > 1) {
                        this.uploadMultipleFile(filesToUpload);
                    } else {
                        this.uploadFile(filesToUpload[0]);
                    }
                }, () => { this.handleCancel(); });
            }, 200);
        }
        return false;
    };

    // 上传多个文件
    async uploadMultipleFile(files: File[]): Promise<void> {
        try {
            this.loading = true;
            const allFiles: UploadResult[] = [];
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('path', this.path);
                let uploadData: UploadResult = {} as UploadResult;
                try {
                    if (this.isDocumentPart) {
                        uploadData = await this.documentService.uploadResponse(formData);
                    } else {
                        uploadData = await this.fileService.uploadFile(formData);
                    }
                    allFiles.push(uploadData);
                    console.log('上传成功', file, uploadData);
                } catch (e) {
                    console.error('上传失败', file);
                }
            }
            console.log('上传完成', allFiles);
            this.uploadSuccess.emit(allFiles);
        } finally {
            this.loading = false;
        }
    }

    async uploadFile(file: File, replace: boolean = false, rename: boolean = false): Promise<void> {
        this.isVisible = false;
        const formData = new FormData();
        formData.append('file', file);
        if (!this.isUploadAvatar && this.isUploadName) {
            formData.append('name', file.name);
        }
        if (replace) {
            formData.append('replace', 'true');
        }
        if (rename) {
            formData.append('rename', 'true');
        }
        formData.append('path', this.path);
        const api = this.isDocumentPart ? this.documentService.upload(formData) : this.fileService.upload(formData);
        this.subscription = api.subscribe((httpEvent: HttpEvent<any>) => {
            console.log('subscription', httpEvent);
            switch (httpEvent.type) {
                case HttpEventType.Sent:
                    break;
                case HttpEventType.ResponseHeader:
                    break;
                case HttpEventType.UploadProgress:
                    this.progress = Math.floor(httpEvent.loaded / (httpEvent.total || 0) * 100);
                    this.upload.emit(this.progress);
                    break;
                case HttpEventType.Response:
                    this.subscription = undefined;
                    this.progress = 0;
                    if (this.batchUpload && this.multiple) {
                        this.uploadSuccess.emit([httpEvent.body]);
                    } else {
                        this.uploadSuccess.emit(httpEvent.body);
                    }
            }
            console.log('httpEvent', httpEvent.type, this.progress);
        }, error => {
            if (error.error.messages?.[0]?.type === 'ERROR') {
                const existMessage = error.error.messages?.find((item: { message: string, type: string }) => item.message.includes('File exists'));
                if (existMessage) {
                    this.resetLoading();
                    this.isVisible = true;
                }
            }
            this.progress = 0;
            this.upload.emit(this.progress);
        });
    }

    resetLoading(): void {
        this.cancelLoading = false;
        this.overwriteLoading = false;
        this.keepLoading = false;
    }

    fileSubmit(type: string): void {
        if (type === 'CANCEL') {
            this.cancelLoading = true;
            this.handleCancel();
        } else if (type === 'OVERWRITE') {
            this.overwriteLoading = true;
            this.handleReplace();
        } else {   // KEEP_BOTH
            this.keepLoading = true;
            this.handleKeep();
        }
    }

    handleRemove = (item: NzUploadFile) => {
        if (this.required && this.fileList.length <= 1) {
            this.message.error('Input is required!');
            return;
        }
        console.log(item);
        this.confirmationService.confirmUpdateTask(this.inactiveUsers, async () => {
            const index = this.fileList.findIndex((file) => file.uid === item.uid);
            this.fileList.splice(index, 1);
            if (!this.isDocumentPart) {
                return;
            }
            await this.documentService.removeFile(item.id);
            this.removeSuccess.emit();
        }, () => { this.handleCancel(); });
    };

    cancelRequest(): void {
        if (!this.subscription) {
            return;
        }
        this.subscription.unsubscribe();
        this.subscription = undefined;
        this.progress = 0;
        this.upload.emit(0);
    }

    handleCancel(): void {
        this.file = undefined;
        this.isVisible = false;
    }

    handleReplace(): void {
        if (this.isDocumentPart || this.existFileReplaceNeedRemove) {
            const index = this.fileList.findIndex((file) => file.name === this.file?.name);
            this.fileList.splice(index, 1);
        }
        this.uploadFile(this.file as File, true, false);
    }

    handleKeep(): void {
        this.uploadFile(this.file as File, false, true);
    }

    handlePreview = async (file: NzUploadFile): Promise<void> => {
        if (!this.canPreview) {
            return;
        }
        this.previewLoading = true;
        this.previewVisible = true;
        this.previewImage = file.url || this.documentService.getPath(file?.id);
        setTimeout(() => {
            this.previewLoading = false;
        }, 500);
    }
}
